import {ModalBody, ModalHeader} from "@heroui/modal";
import {Button} from "@heroui/button";
import React, {useEffect} from "react";
import {useMonitoring} from "@/components/monitoring";
import {Chip} from "@heroui/chip";
import {Bold} from './Bold'
import {Card, CardBody} from "@heroui/card";
import {STARTER_PACK_PRICE, STARTER_PACK_DOWNLOAD_CREDITS, formatPrice} from "@/config/pricing";
import {OutlineCheckCircle} from "@/components/DownloaderApp/UpsellPopup";


function formatDuration(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function Plan({ title, price, recommended, credits, features }) {
    return (<Card className={`flex-grow ${recommended? "border border-gray-700" : ""}`}>
        <div className={"mx-4 my-5 flex flex-col text-left gap-2"}>
            <span className={`text-2xl ${recommended ? "font-bold": ""}`}>{title}</span>
            <span><Bold className={"text-2xl font-bold"}> ${price}</Bold>
                <span className={"text-sm text-gray-400"}> / one-time payment</span>
            </span>
            <b className={"text-sm"}>
                {recommended ? (<Bold>{credits} Sub-only </Bold> ) : (<>{credits} Sub-only </> )}

                download credits</b>
        </div>

        <CardBody>
            <ul className={"gap-2 my-3 flex flex-col text-left text-sm"}>
                {(features ?? []).map((feature, i) => (
                    <li key={i} className={"flex items-center gap-2"}><OutlineCheckCircle /> {feature}</li>
                ))}
            </ul>
        </CardBody>
        <Button className={"mx-5 my-4"} color="secondary" variant={recommended ? undefined : "flat"}
                startContent={<PremiumSvgIcon/>}>
            Get started
        </Button>
    </Card>)
}


const PremiumSvgIcon = (props) => (<svg width="32" height="32" viewBox="0 0 32 32" {...props} fill="white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-3 ml-2 shrink-0 grow-0">
    <path
        d="M17.4999 11.2575L18.6459 13.7338C19.1794 14.8888 20.1067 15.8163 21.2613 16.3501L23.7369 17.4876L21.2613 18.6339C20.1067 19.1676 19.1794 20.0952 18.6459 21.2502L17.4999 23.7265L16.354 21.2502C15.8204 20.0952 14.8932 19.1676 13.7386 18.6339L11.263 17.4876L13.7386 16.3413C14.8932 15.8076 15.8204 14.8801 16.354 13.7338L17.4999 11.2575ZM17.4999 4.9924L13.9747 12.6313C13.7036 13.2175 13.2312 13.6901 12.6451 13.9613L5.00863 17.4876L12.6539 21.0139C13.24 21.2852 13.7123 21.7577 13.9835 22.3439L17.5087 29.9828L21.0339 22.3439C21.3051 21.7577 21.7774 21.2852 22.3635 21.0139L30 17.4876L22.3635 13.9613C21.7774 13.6901 21.3051 13.2175 21.0339 12.6313L17.5087 4.9924H17.4999Z"></path>
    <path
        d="M7.37091 2L5.85761 5.29006C5.74389 5.54381 5.5427 5.74506 5.28903 5.85882L2 7.37259L5.28903 8.88637C5.5427 9.00012 5.74389 9.20137 5.85761 9.45513L7.37091 12.7364L8.88422 9.44638C8.99793 9.19262 9.19912 8.99137 9.4528 8.87762L12.7418 7.36384L9.4528 5.85007C9.19912 5.73631 8.99793 5.53506 8.88422 5.28131L7.37091 2Z"></path>
</svg>)

export function showTwitchUpsellModal(show, data = {}) {
    return show({
        component: TwitchUpsellPopup,
        data: {
            vodId: data.vodId || null,
            source: data.source || 'twitch-direct-download',
            isExternal: data.isExternal ?? false,
            scrollBehavior: "inside",
            className: "min-h-2/5",
            size: '4xl',
        },
    })
}


export function TwitchUpsellPopup({data, onClose}) {
    const {track} = useMonitoring()

    const [isLoading, setIsLoading] = React.useState(false)

    const contextVodId = null

    useEffect(() => {
        track('TwitchUpsellPopup opened', {
            source: data.source
        })
    }, []);

    useEffect(() => {
        try {
            window.posthog?.startSessionRecording();
        } catch (err) {
        }

        return () => {
            try {
                window.posthog?.stopSessionRecording();
            } catch (err) {
            }
        }
    }, []);

    const total_downloads = STARTER_PACK_DOWNLOAD_CREDITS;

    const chipElem = (<Chip radius="sm" startContent={<PremiumSvgIcon/>}>Subscription-only</Chip>)

    let CTA_LINK = `https://kick-video-download.lemonsqueezy.com/buy/0a65e7f8-7959-437e-b613-0547d366fb0a?discount=1`;
    CTA_LINK = '/api/stripe/checkout?platform=twitch';

    function onPressed() {
        track('[Premium] TwitchUpsellPopup > CTA clicked', {
            source: data.source
        });
        setIsLoading(true);
        if(data.isExternal) {
            window.open(CTA_LINK);
            onClose();
        } else {
            window.location.href = CTA_LINK;
        }

    }

    const starterPackFeatures = [
        (<span><Bold>$0,25</Bold> / VOD ({STARTER_PACK_DOWNLOAD_CREDITS} credits)</span>),
        <span>Works with every Twitch creator</span>,
        <span>One-time payment, no recurring costs</span>,
        <span>No hidden fees, no surprises</span>
    ]
    const fanPackFeatures = [
        (<span><Bold>$0,20</Bold> / VOD (30 credits)</span>),
        <span>Works with every Twitch creator</span>,
        <span>One-time payment, no recurring costs</span>,
        <span>No hidden fees, no surprises</span>
    ]

    const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(null);
    const [videoTitle, setVideoTitle] = React.useState<string | null>(null);
    const [videoDuration, setVideoDuration] = React.useState<number | null>(null);
    const [previewIndex, setPreviewIndex] = React.useState(0);
    const [tileFade, setTileFade] = React.useState(true);

    const previewPositions = React.useMemo(() => {
        if (!videoDuration || videoDuration <= 0) return [0];
        const maxTilesPerSheet = 5 * 50;
        const totalTiles = Math.floor(videoDuration / 60);
        const percentages = [0, 0.2, 0.4, 0.6, 0.8];
        return percentages
            .map(p => Math.min(Math.floor(p * totalTiles), maxTilesPerSheet - 1))
            .filter((v, i, arr) => arr.indexOf(v) === i);
    }, [videoDuration]);

    useEffect(() => {
        if (!thumbnailUrl || previewPositions.length <= 1) return;
        const interval = setInterval(() => {
            setTileFade(false);
            setTimeout(() => {
                setPreviewIndex(prev => (prev + 1) % previewPositions.length);
                setTileFade(true);
            }, 300);
        }, 3000);
        return () => clearInterval(interval);
    }, [thumbnailUrl, previewPositions]);

    return (
        <>
            <ModalHeader className="flex flex-col gap-1">
        <span id="modal-title">
        </span>
            </ModalHeader>
            <ModalBody className="my-2">
            <div className="flex flex-col justify-center items-center h-full gap-10 my-5">

                <div class={"flex flex-col gap-1"}>
                    <b className={"text-xl text-center"}>Unlock <Bold> Subscription-only</Bold> Twitch VODs Without a Monthly Subscription</b>
                </div>


                <b className={"text-2xl"}>Get <Bold>{STARTER_PACK_DOWNLOAD_CREDITS} download credits</Bold> for just <Bold>{formatPrice(STARTER_PACK_PRICE)} 🎉</Bold></b>


                <div className={"flex flex-col-reverse md:flex-row gap-6 items-center"}>

                    <div className={`${thumbnailUrl ? 'md:basis-2/3' : ''} flex items-center`}>
                        <div className={"flex flex-col gap-2"}>
                            <span className={"text-sm"}>Benefits:</span>
                            <ul className={"gap-2 flex flex-col text-left text-sm"}>
                                <li className={"flex items-start gap-2"}><OutlineCheckCircle
                                    className="shrink-0 mt-0.5"
                                    style={{color: '#51fc1a'}}/>
                                    <span><Bold>Significant Savings:</Bold> {STARTER_PACK_DOWNLOAD_CREDITS} download credits = {STARTER_PACK_DOWNLOAD_CREDITS} subscription-only VODs</span>
                                </li>
                                <li className={"flex items-start gap-2"}><OutlineCheckCircle
                                    className="shrink-0 mt-0.5"
                                    style={{color: '#51fc1a'}}/>
                                    <span><Bold>Zero Commitment:</Bold> One-time payment. Absolutely no recurring fees</span>
                                </li>
                                <li className={"flex items-start gap-2"}><OutlineCheckCircle
                                    className="shrink-0 mt-0.5"
                                    style={{color: '#51fc1a'}}/>
                                    <span><Bold>Flexible:</Bold> Credits stay valid for 3 full months</span>
                                </li>
                                <li className={"flex items-start gap-2"}><OutlineCheckCircle
                                    className="shrink-0 mt-0.5"
                                    style={{color: '#51fc1a'}}/>
                                    <span><Bold>Fair Play:</Bold> Re-downloading the same video never costs extra</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                    {
                        thumbnailUrl ? (() => {
                            const cols = 5;
                            const idx = previewPositions[previewIndex];
                            const col = idx % cols;
                            const row = Math.floor(idx / cols);
                            return (<div className="md:basis-1/3 flex flex-col items-center justify-center gap-2">
                                <div className="relative overflow-hidden rounded-lg border border-white/20" style={{width: 216, height: 121}}>
                                    <img
                                        src={thumbnailUrl}
                                        style={{
                                            width: 180,
                                            height: 101,
                                            objectFit: 'none',
                                            objectPosition: `${-col * 180}px ${-row * 101}px`,
                                            transformOrigin: 'top left',
                                            transform: 'scale(1.2)',
                                            opacity: tileFade ? 1 : 0,
                                            transition: 'opacity 0.3s ease-in-out',
                                        }}
                                        alt="VOD preview"
                                    />
                                    <span className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                                        Preview
                                    </span>
                                    {videoDuration > 0 && (
                                        <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-xs font-medium px-1.5 py-0.5 rounded">
                                            {formatDuration(videoDuration)}
                                        </span>
                                    )}
                                </div>
                                <span className="h-[16px] text-xs text-neutral-300 text-left truncate w-[216px]">{videoTitle ? videoTitle : ''}</span>
                            </div>);
                        })() : null
                    }
                </div>

                <div className={"flex flex-row gap-4 items-center "}>
                    <Button isDisabled={isLoading} isLoading={isLoading} onPress={onPressed} color="secondary"
                            startContent={<PremiumSvgIcon/>}>
                        <span>Unlock Access ({formatPrice(STARTER_PACK_PRICE)})</span>
                    </Button>
                    {
                        (!contextVodId) ? null : <>
                            <Button isDisabled={isLoading} isLoading={isLoading} onPress={() => {
                                window.open(`/api/get-premium-content-sample?uuid=${contextVodId}`)
                            }} color="default">
                                <span>Download this VOD FREE sample (10 seconds)</span>
                            </Button>
                        </>
                    }
                </div>

            </div>
            </ModalBody>
        </>
    );
}
